import { Badge } from "@/components/badge";
import { useProModal } from "@/components/pro-alert-box";
import { TextareaInput } from "@/components/textarea-input";
import { Tooltip } from "@/components/tooltip";
import { IS_PRO } from "@/utils/constants";
import { css } from "@/utils/css";
import { TONES } from "@/utils/tones";
import { Icon } from "@wordpress/components";
import { __ } from "@wordpress/i18n";

type Props = React.HTMLProps<HTMLDivElement> & {
    tone: (typeof TONES)[0];
    setTone(tone: (typeof TONES)[0]): void;
    modifiedTone: string | undefined;
    setModifiedTone(tone: string | undefined): void;
};

export function Tone({ tone, setTone, modifiedTone, setModifiedTone, className }: Props) {
    const { showProAlert } = useProModal();

    return (
        <>
            <div className={css("flex justify-start flex-wrap gap-2 my-4", className)}>
                {TONES.map((t) => {
                    const isSelected = t.tone === tone.tone;

                    return (
                        <Badge
                            onClick={() => {
                                setTone(t);
                                setModifiedTone(undefined);
                            }}
                            isActive={isSelected}
                        >
                            {t.label}
                            {isSelected && (
                                <Tooltip
                                    key={t.tone}
                                    title={__("Modify Tone", "antimanual")}
                                    render={(props) => {
                                        return (
                                            <span
                                                className={css("pl-1")}
                                                onClick={(e) => {
                                                    e.stopPropagation();

                                                    if (!IS_PRO) {
                                                        showProAlert(
                                                            __("Premium Feature", "antimanual"),
                                                            __(
                                                                "Tone modification is a premium feature. Upgrade to modify the selected tone as you want.",
                                                                "antimanual"
                                                            )
                                                        );
                                                        return;
                                                    }

                                                    if (!modifiedTone) {
                                                        setModifiedTone(TONES.find((t2) => t.tone === t2.tone)?.description);
                                                    }
                                                }}
                                                {...props}
                                            >
                                                <Icon className={css("badge-icon")} icon={"edit"} />
                                            </span>
                                        );
                                    }}
                                ></Tooltip>
                            )}
                        </Badge>
                    );
                })}
            </div>
            {modifiedTone && (
                <TextareaInput
                    help={__("Modify the selected tone as you like.", "antimanual")}
                    placeholder={__(
                        "Adopt a tone like a course instructor or tutor. Explain concepts step by step, and guide the user as if teaching a class.",
                        "antimanual"
                    )}
                    value={modifiedTone || TONES.find((t) => t.tone === tone.tone)?.description || ""}
                    onChange={setModifiedTone}
                />
            )}
        </>
    );
}
